import type {
	ApprovalWorkflowResponseApi,
	ApprovalWorkflowListResponseApi,
	ApprovalWorkflowStageResponseApi,
	CreateApprovalWorkflowRequestApi,
	UpdateApprovalWorkflowRequestApi,
	CreateApprovalWorkflowStageRequestApi,
	UpdateApprovalWorkflowStageRequestApi,
} from "../models/approvalWorkflow";
import type { UserListItemApi } from "../models/user";

export interface WorkflowAssignee {
	id: string;
	type: "named";
	userId: string;
	name: string;
	email: string;
}

export type ApprovalRule = "any" | "all";

export interface WorkflowStage {
	id: string;
	name: string;
	order: number;
	assignees: WorkflowAssignee[];
	rule: ApprovalRule;
	slaDays: number | null;
	escalateToUserId: string | null;
	escalateToName: string | null;
}

export interface RoutingRule {
	minNetImpact: number | null;
	maxNetImpact: number | null;
	categories: string[];
}

export interface WorkflowDefinition {
	id: string;
	name: string;
	description: string;
	isDefault: boolean;
	isActive: boolean;
	routing: RoutingRule;
	stages: WorkflowStage[];
}

function resolveUser(
	userId: string,
	users: UserListItemApi[],
): { name: string; email: string } {
	const user = users.find((u) => u.userId === userId);
	return {
		name: user?.name ?? `User ${userId}`,
		email: user?.email ?? "",
	};
}

function mapStageFromApi(
	stage: ApprovalWorkflowStageResponseApi,
	users: UserListItemApi[],
): WorkflowStage {
	const escalateUser = stage.escalateToUserId
		? resolveUser(stage.escalateToUserId, users)
		: null;

	return {
		id: String(stage.id),
		name: stage.name,
		order: stage.stageOrder,
		assignees: stage.assignees.map((a) => {
			const resolved = resolveUser(a.userId, users);
			return {
				id: String(a.id),
				type: "named" as const,
				userId: a.userId,
				name: resolved.name,
				email: resolved.email,
			};
		}),
		rule: stage.approvalRule === "all" ? "all" : "any",
		slaDays: stage.slaDays || null,
		escalateToUserId: stage.escalateToUserId ?? null,
		escalateToName: escalateUser?.name ?? null,
	};
}

export function mapWorkflowFromApi(
	workflow: ApprovalWorkflowResponseApi,
	users: UserListItemApi[],
): WorkflowDefinition {
	return {
		id: String(workflow.id),
		name: workflow.name,
		description: workflow.description ?? "",
		isDefault: workflow.isDefault,
		isActive: workflow.isActive,
		routing: {
			minNetImpact: workflow.minNetImpact != null ? Number(workflow.minNetImpact) : null,
			maxNetImpact: workflow.maxNetImpact != null ? Number(workflow.maxNetImpact) : null,
			categories: workflow.scenarioCategory ? [workflow.scenarioCategory] : [],
		},
		stages: workflow.stages
			.slice()
			.sort((a, b) => a.stageOrder - b.stageOrder)
			.map((s) => mapStageFromApi(s, users)),
	};
}

export function mapWorkflowsFromApi(
	response: ApprovalWorkflowListResponseApi,
	users: UserListItemApi[],
): WorkflowDefinition[] {
	return response.workflows.map((w) => mapWorkflowFromApi(w, users));
}

export function toCreateWorkflowRequest(
	wf: WorkflowDefinition,
): CreateApprovalWorkflowRequestApi {
	return {
		name: wf.name,
		description: wf.description || null,
		isDefault: wf.isDefault,
		isActive: wf.isActive,
		scenarioCategory: wf.routing.categories.length > 0 ? wf.routing.categories[0] : null,
		minNetImpact: wf.routing.minNetImpact,
		maxNetImpact: wf.routing.maxNetImpact,
	};
}

export function toUpdateWorkflowRequest(
	wf: WorkflowDefinition,
): UpdateApprovalWorkflowRequestApi {
	return {
		name: wf.name,
		description: wf.description || null,
		isDefault: wf.isDefault,
		isActive: wf.isActive,
		scenarioCategory: wf.routing.categories.length > 0 ? wf.routing.categories[0] : null,
		minNetImpact: wf.routing.minNetImpact,
		maxNetImpact: wf.routing.maxNetImpact,
	};
}

export function toCreateStageRequest(
	stage: WorkflowStage,
): CreateApprovalWorkflowStageRequestApi {
	return {
		name: stage.name,
		stageOrder: stage.order,
		approvalRule: stage.rule,
		slaDays: stage.slaDays,
		escalateToUserId: stage.escalateToUserId,
		assigneeUserIds: stage.assignees.map((a) => a.userId),
	};
}

export function toUpdateStageRequest(
	stage: WorkflowStage,
): UpdateApprovalWorkflowStageRequestApi {
	return {
		name: stage.name,
		stageOrder: stage.order,
		approvalRule: stage.rule,
		slaDays: stage.slaDays,
		escalateToUserId: stage.escalateToUserId,
		assigneeUserIds: stage.assignees.map((a) => a.userId),
	};
}
